import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Image, FormCheck, Button } from 'react-bootstrap';
import { useLazyGetUploadedAssetsQuery, useUploadAssetMutation } from '../../store/api/editor.api';
import { useBackgroundRemovalMutation } from '../../store/api/apprentice.api';
import { displayErrorToast } from '../../utils/ToastUtils';
import { LoadingSpinner } from '../../pages/Apprentice/AppereancticeSpinners';
import { IUploadedAsset } from '../../interfaces/assets.interface';
import { useAppDispatch, useAppSelector } from '../../store/store';
import { getActiveBrand } from '../../store/selector/brand.selector';
import { addUploadedAssetToStore } from '../../utils/assets.utils';

interface IProps {
    handleNo: () => void;
    tempImageUrl: IUploadedAsset | null;
    setTempImageUrl: React.Dispatch<React.SetStateAction<IUploadedAsset | null>>;
    tempImageFile: File | null;
    setTempImageFile: React.Dispatch<React.SetStateAction<File | null>>;
    onHide: () => void;
    onUpload: (imageUrl: string) => void;
}

const ShowSelectedImage = ({
    handleNo,
    tempImageUrl,
    setTempImageUrl,
    tempImageFile,
    setTempImageFile,
    onHide,
    onUpload,
}: IProps) => {
    const dispatch = useAppDispatch();
    const activeBrand = useAppSelector(getActiveBrand);
    const [uploadAsset, { isLoading: uploadAssetLoading }] = useUploadAssetMutation();
    const [backgroundRemoval] = useBackgroundRemovalMutation();

    const [backgroudRemoveLoading, setBackgroudRemoveLoading] = useState<boolean>(false);
    const [isBgReplaceSelected, setIsBgReplaceSelected] = useState<boolean>(false);

    const saveAssetImageFile = async () => {
        if (tempImageFile && activeBrand) {
            await uploadAsset({ image: tempImageFile, brandId: activeBrand.id }).then(
                (res: any) => {
                    if (res && 'data' in res) addUploadedAssetToStore(res.data, dispatch);
                    const imageUrl = res?.data?.image;
                    setTempImageUrl(null);
                    setTempImageFile(null);
                    onUpload(imageUrl);
                    onHide();
                },
            );
        }
    };

    const removeBackgroundFunc = async () => {
        if ((!tempImageUrl && !tempImageFile) || !activeBrand) return;

        setBackgroudRemoveLoading(true);
        const formData = new FormData();

        if (tempImageFile) {
            formData.append('fileInput', tempImageFile);
        } else if (tempImageUrl) {
            formData.append('previousUpload', tempImageUrl.name);
        }
        formData.append('brandId', activeBrand.id.toString());

        const response: any = await backgroundRemoval(formData);
        if (response?.data?.body?.remove_bg_url) {
            const regexToExractFileName = /\/(-\d+)\?/;

            const match = response.data.body.remove_bg_url.match(regexToExractFileName);
            setTempImageUrl({
                id: 12345,
                assets: response?.data?.body?.remove_bg_url,
                name: 'random name',
            });
            setTempImageFile(null);
            setIsBgReplaceSelected(false);
            setBackgroudRemoveLoading(false);
        } else if (response.error) {
            const message = response.error?.message;
            setBackgroudRemoveLoading(false);
            displayErrorToast(message);
        }
    };

    const handleUploadImage = async () => {
        if (tempImageFile) {
            await saveAssetImageFile();
        } else if (tempImageUrl) {
            const image = tempImageUrl;
            setTempImageUrl(null);
            setTempImageFile(null);
            onUpload(image.assets);
            onHide();
        }
    };

    const getImageSource = () => {
        return tempImageFile ? URL.createObjectURL(tempImageFile) : tempImageUrl?.assets;
    };

    const button_name = backgroudRemoveLoading
        ? 'Processing...'
        : isBgReplaceSelected
          ? 'Remove Background'
          : tempImageFile
            ? 'Upload and Select'
            : 'Select';

    return (
        <>
            <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ duration: 0.5 }}
                className="m-0 mb-3 d-flex align-items-center justify-content-center mx-auto"
                style={{ width: '55%' }}
            >
                <div className="mt-2 image-delete position-relative">
                    <Image src={getImageSource() as string} className="border border-grey" />
                    {backgroudRemoveLoading && (
                        <motion.div
                            key="overlay"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="overlay position-absolute top-0 right-0 left-0 bottom-0 d-flex flex-column justify-content-center align-items-center w-100 rounded"
                            style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
                        >
                            <LoadingSpinner show={backgroudRemoveLoading} />
                            <p className="text-white">Processing your image...</p>
                        </motion.div>
                    )}
                    <button
                        className="iconbtn iconbtn-transparent"
                        onClick={(event) => {
                            event.stopPropagation();
                            handleNo();
                        }}
                    >
                        <i className="ph-bold ph-prohibit-inset"></i>
                    </button>
                </div>
            </motion.div>
            <FormCheck
                type="checkbox"
                id="removeBackground"
                label="Remove Background"
                defaultChecked={isBgReplaceSelected}
                onClick={() => setIsBgReplaceSelected(!isBgReplaceSelected)}
            />
            <div className="d-flex justify-content-end gap-2">
                {!backgroudRemoveLoading && !uploadAssetLoading && (
                    <Button variant="outline-primary" onClick={() => handleNo()}>
                        No
                    </Button>
                )}
                <Button
                    variant="primary"
                    onClick={() => {
                        if (isBgReplaceSelected) removeBackgroundFunc();
                        else handleUploadImage();
                    }}
                    disabled={backgroudRemoveLoading || uploadAssetLoading}
                >
                    {uploadAssetLoading ? (
                        <LoadingSpinner show={uploadAssetLoading} />
                    ) : (
                        button_name
                    )}
                </Button>
            </div>
        </>
    );
};

export default ShowSelectedImage;
